import type { BoggleLocalProgressStore } from './localProgressStore'
import { normalizeCompletedRunPathStep } from './acceptedWordFacts'
import type {
  BoggleBadgeProgressProjection,
  BoggleCompletedRun,
  BoggleUnviewedAchievementTier,
  BoggleUnviewedBadge,
  BoggleUnviewedWord,
  BoggleWordOccurrence,
  BoggleWordProjectionSnapshot,
  BoggleWordStat,
  PlayerAchievementState,
} from './types'

function cloneCompletedRun(run: BoggleCompletedRun): BoggleCompletedRun {
  return {
    ...run,
    acceptedWordTimeline: run.acceptedWordTimeline?.map(word => ({
      ...word,
      clearedPositions: word.clearedPositions.map(position => ({ ...position })),
      path: word.path.map(position => ({ ...position })),
      pathSteps: word.pathSteps?.map(step => {
        const normalized = normalizeCompletedRunPathStep(step)
        return {
          ...normalized,
          effectiveSpecialKinds: [...normalized.effectiveSpecialKinds],
          physicalSpecialKinds: [...normalized.physicalSpecialKinds],
          position: { ...normalized.position },
          virtualSpecialKinds: [...normalized.virtualSpecialKinds],
        }
      }),
      effectiveSpecialTileCountsUsed: word.effectiveSpecialTileCountsUsed
        ? { ...word.effectiveSpecialTileCountsUsed }
        : undefined,
      physicalSpecialTileCountsUsed: word.physicalSpecialTileCountsUsed
        ? { ...word.physicalSpecialTileCountsUsed }
        : undefined,
      specialTileCountsUsed: word.specialTileCountsUsed
        ? { ...word.specialTileCountsUsed }
        : undefined,
      tileLetters: [...word.tileLetters],
    })),
    actionTimeline: run.actionTimeline?.map(action =>
      action.actionKind === 'selected-letter'
        ? {
            ...action,
            position: { ...action.position },
          }
        : { ...action },
    ),
    earnedBadges: run.earnedBadges?.map(earned => ({ ...earned })),
    runEnd: run.runEnd ? { ...run.runEnd } : undefined,
    setupSnapshot: run.setupSnapshot
      ? (JSON.parse(
          JSON.stringify(run.setupSnapshot),
        ) as BoggleCompletedRun['setupSnapshot'])
      : undefined,
    submittedWords: [...run.submittedWords],
    summarySnapshot: run.summarySnapshot
      ? {
          effectiveSpecialTileCounts: run.summarySnapshot.effectiveSpecialTileCounts
            ? { ...run.summarySnapshot.effectiveSpecialTileCounts }
            : undefined,
          goldTilesPlayed: run.summarySnapshot.goldTilesPlayed,
          physicalGoldTilesPlayed: run.summarySnapshot.physicalGoldTilesPlayed,
          physicalSpecialTileCounts: run.summarySnapshot.physicalSpecialTileCounts
            ? { ...run.summarySnapshot.physicalSpecialTileCounts }
            : undefined,
          specialTileCounts: run.summarySnapshot.specialTileCounts
            ? { ...run.summarySnapshot.specialTileCounts }
            : undefined,
          tierOutcomes: run.summarySnapshot.tierOutcomes.map(outcome => ({
            ...outcome,
          })),
          total: run.summarySnapshot.total,
          totalMeterEarned: run.summarySnapshot.totalMeterEarned,
          totalOvermeterEarned: run.summarySnapshot.totalOvermeterEarned,
          uncommonLettersPlayed: run.summarySnapshot.uncommonLettersPlayed,
          virtualGoldTilesPlayed: run.summarySnapshot.virtualGoldTilesPlayed,
        }
      : undefined,
  }
}

function cloneOccurrence(row: BoggleWordOccurrence): BoggleWordOccurrence {
  return { ...row }
}

function cloneModeCounts(stat: BoggleWordStat): BoggleWordStat['perModeCounts'] {
  return Object.fromEntries(
    Object.entries(stat.perModeCounts).map(([gameModeId, counts]) => [
      gameModeId,
      { ...counts },
    ]),
  ) as BoggleWordStat['perModeCounts']
}

function cloneStat(stat: BoggleWordStat): BoggleWordStat {
  return {
    ...stat,
    lexicalTags: [...stat.lexicalTags],
    perModeCounts: cloneModeCounts(stat),
  }
}

function cloneBadgeProgress(
  progress: BoggleBadgeProgressProjection,
): BoggleBadgeProgressProjection {
  return {
    perBadge: Object.fromEntries(
      Object.entries(progress.perBadge).map(([badgeId, record]) => [
        badgeId,
        record
          ? {
              countsByRating: { ...record.countsByRating },
              highestRating: record.highestRating,
            }
          : record,
      ]),
    ),
    profileRunsByRating: { ...progress.profileRunsByRating },
  }
}

function cloneUnviewedBadge(entry: BoggleUnviewedBadge): BoggleUnviewedBadge {
  return { ...entry }
}

function cloneUnviewedAchievementTier(
  entry: BoggleUnviewedAchievementTier,
): BoggleUnviewedAchievementTier {
  return { ...entry }
}

function cloneUnviewedWord(entry: BoggleUnviewedWord): BoggleUnviewedWord {
  return { ...entry }
}

function clonePlayerAchievementState(
  state: PlayerAchievementState,
): PlayerAchievementState {
  return {
    backfilledTierIds: [...state.backfilledTierIds],
    revealedRatingIds: [...state.revealedRatingIds],
    schemaVersion: state.schemaVersion,
    unlockedTierIds: [...state.unlockedTierIds],
  }
}

export class MemoryBoggleLocalProgressStore implements BoggleLocalProgressStore {
  private badgeProgress: BoggleBadgeProgressProjection | null = null
  private completedRuns = new Map<string, BoggleCompletedRun>()
  private playerAchievementState: PlayerAchievementState | null = null
  private unviewedAchievementTiers = new Map<string, BoggleUnviewedAchievementTier>()
  private unviewedBadges = new Map<string, BoggleUnviewedBadge>()
  private unviewedWords = new Map<string, BoggleUnviewedWord>()
  private wordOccurrences = new Map<string, BoggleWordOccurrence>()
  private wordStats = new Map<string, BoggleWordStat>()

  async clearUnviewedAchievementTiers() {
    this.unviewedAchievementTiers.clear()
  }

  async clearUnviewedBadges() {
    this.unviewedBadges.clear()
  }

  async clearUnviewedWords() {
    this.unviewedWords.clear()
  }

  async getBadgeProgress() {
    return this.badgeProgress ? cloneBadgeProgress(this.badgeProgress) : null
  }

  async getCompletedRun(id: string) {
    const run = this.completedRuns.get(id)
    return run ? cloneCompletedRun(run) : undefined
  }

  async getCompletedRuns() {
    return [...this.completedRuns.values()].map(cloneCompletedRun)
  }

  async getPlayerAchievementState() {
    return this.playerAchievementState
      ? clonePlayerAchievementState(this.playerAchievementState)
      : null
  }

  async getUnviewedAchievementTiers() {
    return [...this.unviewedAchievementTiers.values()].map(cloneUnviewedAchievementTier)
  }

  async getUnviewedBadges() {
    return [...this.unviewedBadges.values()].map(cloneUnviewedBadge)
  }

  async getUnviewedWords() {
    return [...this.unviewedWords.values()].map(cloneUnviewedWord)
  }

  async getWordOccurrences() {
    return [...this.wordOccurrences.values()].map(cloneOccurrence)
  }

  async getWordStats() {
    return [...this.wordStats.values()].map(cloneStat)
  }

  async putCompletedRun(run: BoggleCompletedRun) {
    this.completedRuns.set(run.id, cloneCompletedRun(run))
  }

  async putPlayerAchievementState(state: PlayerAchievementState) {
    this.playerAchievementState = clonePlayerAchievementState(state)
  }

  async replaceBadgeProgressProjection(projection: BoggleBadgeProgressProjection) {
    this.badgeProgress = cloneBadgeProgress(projection)
  }

  async replaceUnviewedAchievementTiers(
    entries: readonly BoggleUnviewedAchievementTier[],
  ) {
    this.unviewedAchievementTiers = new Map(
      entries.map(entry => [entry.tierId, cloneUnviewedAchievementTier(entry)]),
    )
  }

  async replaceWordProjections(projections: BoggleWordProjectionSnapshot) {
    this.wordOccurrences = new Map(
      projections.wordOccurrences.map(row => [row.id, cloneOccurrence(row)]),
    )
    this.wordStats = new Map(
      projections.wordStats.map(row => [row.normalizedWord, cloneStat(row)]),
    )
  }

  async replaceUnviewedBadges(entries: readonly BoggleUnviewedBadge[]) {
    this.unviewedBadges = new Map(
      entries.map(entry => [entry.badgeId, cloneUnviewedBadge(entry)]),
    )
  }

  async replaceUnviewedWords(entries: readonly BoggleUnviewedWord[]) {
    this.unviewedWords = new Map(
      entries.map(entry => [entry.normalizedWord, cloneUnviewedWord(entry)]),
    )
  }
}
